import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bike, RotateCcw, ShoppingBag, Smartphone, Store } from 'lucide-react';
import { cn } from '@lib/utils';
import { opsService, type AppVersionRow } from '@services/ops.service';

/**
 * Per app × platform version management. Raising min_version force-updates
 * every user of that platform on their next app launch (the apps gate on
 * GET /apps/versions with a blocking "update required" screen).
 *
 * Layout: one card per app (customer / merchant / rider) with its Android and
 * iOS rows stacked inside, instead of six identical loose cards.
 */

const APP_ORDER = ['customer', 'merchant', 'rider'];
const PLATFORM_ORDER = ['android', 'ios'];

const appIcons: Record<string, React.ElementType> = {
  customer: ShoppingBag,
  merchant: Store,
  rider: Bike,
};

const platformLabel = (p: string) =>
  p === 'ios' ? 'iOS' : p.charAt(0).toUpperCase() + p.slice(1);

const orderIndex = (order: string[], v: string) => {
  const i = order.indexOf(v);
  return i === -1 ? order.length : i;
};

export const AppVersionsPage = () => {
  const queryClient = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['app-versions'],
    queryFn: opsService.appVersions,
  });

  const apps = [...new Set(rows.map((r) => r.app))].sort(
    (a, b) => orderIndex(APP_ORDER, a) - orderIndex(APP_ORDER, b) || a.localeCompare(b)
  );

  const onSaved = () => queryClient.invalidateQueries({ queryKey: ['app-versions'] });

  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          Operations
        </p>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] mt-1">
          App versions
        </h1>
        <p className="text-[14px] text-[var(--text-muted)] mt-1 max-w-2xl">
          Raising a minimum version forces every user of that app/platform to update
          before they can continue. Latest version powers the soft &ldquo;update
          available&rdquo; nudge.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-3xl bg-white shadow-soft h-[420px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 items-start md:grid-cols-2 xl:grid-cols-3">
          {apps.map((app) => (
            <AppCard
              key={app}
              app={app}
              rows={rows
                .filter((r) => r.app === app)
                .sort(
                  (a, b) =>
                    orderIndex(PLATFORM_ORDER, a.platform) - orderIndex(PLATFORM_ORDER, b.platform) ||
                    a.platform.localeCompare(b.platform)
                )}
              onSaved={onSaved}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── One card per app, platforms stacked inside ────────────────────────────────

function AppCard({ app, rows, onSaved }: { app: string; rows: AppVersionRow[]; onSaved: () => void }) {
  const Icon = appIcons[app] ?? Smartphone;

  return (
    <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
      {/* App header */}
      <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="text-[15px] font-semibold text-[var(--text-primary)] capitalize">
            {app} app
          </h2>
          <p className="text-[12px] text-[var(--text-muted)]">
            {rows.map((r) => platformLabel(r.platform)).join(' · ')}
          </p>
        </div>
      </div>

      <div className="divide-y divide-[var(--border-subtle)]">
        {rows.map((row) => (
          <PlatformSection key={`${row.app}-${row.platform}`} row={row} onSaved={onSaved} />
        ))}
      </div>
    </div>
  );
}

// ── One platform row (android / ios) with its own dirty state ─────────────────

const inputCls =
  'w-full rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3.5 py-2.5 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--border-strong)] transition-colors duration-200';
const labelCls =
  'block text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-1.5';

function PlatformSection({ row, onSaved }: { row: AppVersionRow; onSaved: () => void }) {
  const [minVersion, setMinVersion] = useState(row.min_version);
  const [latestVersion, setLatestVersion] = useState(row.latest_version);
  const [storeUrl, setStoreUrl] = useState(row.store_url ?? '');
  const [message, setMessage] = useState(row.message ?? '');

  const dirty =
    minVersion !== row.min_version ||
    latestVersion !== row.latest_version ||
    storeUrl !== (row.store_url ?? '') ||
    message !== (row.message ?? '');

  const save = useMutation({
    mutationFn: () =>
      opsService.updateAppVersion({
        app: row.app,
        platform: row.platform,
        minVersion,
        latestVersion,
        storeUrl: storeUrl || undefined,
        message: message || undefined,
      }),
    onSuccess: onSaved,
  });

  const revert = () => {
    setMinVersion(row.min_version);
    setLatestVersion(row.latest_version);
    setStoreUrl(row.store_url ?? '');
    setMessage(row.message ?? '');
    save.reset();
  };

  return (
    <div className="px-6 py-5">
      {/* Platform badge + last-updated hint */}
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-bold uppercase tracking-[0.08em]">
          <Smartphone className="w-3 h-3" strokeWidth={1.5} />
          {platformLabel(row.platform)}
        </span>
        <span className="text-[11px] text-[var(--text-muted)]">
          Updated {new Date(row.updated_at).toLocaleDateString('en-KE', {
            day: '2-digit', month: 'short', year: 'numeric',
          })}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={labelCls}>
            Minimum{' '}
            <span className="font-normal normal-case tracking-normal">(forces update)</span>
          </label>
          <input className={cn(inputCls, 'font-mono')} value={minVersion}
            onChange={(e) => setMinVersion(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Latest</label>
          <input className={cn(inputCls, 'font-mono')} value={latestVersion}
            onChange={(e) => setLatestVersion(e.target.value)} />
        </div>
      </div>

      <div className="mb-3">
        <label className={labelCls}>Store URL</label>
        <input className={inputCls} value={storeUrl}
          onChange={(e) => setStoreUrl(e.target.value)}
          placeholder="https://play.google.com/store/apps/details?id=…" />
      </div>

      <div className="mb-4">
        <label className={labelCls}>
          Update message{' '}
          <span className="font-normal normal-case tracking-normal">(blocking screen)</span>
        </label>
        <input className={inputCls} value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Please update to keep receiving orders." />
      </div>

      {/* Action row: status hint left, revert + save right */}
      <div className="flex items-center justify-between gap-3">
        <p className={cn(
          'text-[12px] font-medium min-h-[1rem]',
          save.isError ? 'text-[var(--state-danger)]'
            : dirty ? 'text-[var(--state-warning)]'
            : save.isSuccess ? 'text-[var(--state-success)]'
            : 'text-[var(--text-muted)]'
        )}>
          {save.isPending ? 'Saving…'
            : save.isError ? 'Failed to save.'
            : dirty ? 'Unsaved changes'
            : save.isSuccess ? 'Saved.'
            : ''}
        </p>
        <div className="flex items-center gap-2">
          {dirty && !save.isPending && (
            <button
              onClick={revert}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-3.5 py-2 text-[12px] font-semibold text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
              title="Revert to saved values"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              Revert
            </button>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[12px] font-semibold text-white transition-all duration-300 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
